<template>
  <div class="rv-page">
    <!-- Alert Banner -->
    <AAlertBanner :messages="alertError" hide-title />

    <!-- Submit Confirmation Dialog -->
    <TheModalConfirmation ref="confirmDialog" />

    <!-- Revert Case Dialog (custom — has textarea) -->
    <v-dialog v-model="revertCaseDialog" max-width="460" content-class="rv-revert-dialog">
      <div class="rv-revert-card">
        <div class="rv-revert-card__header">
          <div class="rv-revert-card__icon-wrap">
            <v-icon color="#f59e0b" size="22">mdi-file-undo</v-icon>
          </div>
          <h3 class="rv-revert-card__title">Revert Case back to taxPOD Admin</h3>
        </div>

        <div class="rv-revert-card__body">
          <label class="rv-revert-card__label">
            <span class="text-danger">*</span> Please confirm if you wish to reject the refund &amp; add a remarks:
          </label>
          <v-textarea
            filled
            no-resize
            name="input-7-4"
            v-model="rejectReason"
            :error-messages="errorField.rejectReason"
            hide-details="auto"
            class="rv-input"
            rows="4"
            placeholder="Enter remarks..."
          ></v-textarea>
        </div>

        <div class="rv-revert-card__actions">
          <button
            class="rv-revert-card__btn rv-revert-card__btn--cancel"
            @click="revertCaseDialog = false"
          >
            Cancel
          </button>
          <button
            class="rv-revert-card__btn rv-revert-card__btn--confirm"
            @click="revertCase()"
          >
            Confirm
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Header Card -->
    <div class="rv-header">
      <h1 v-if="!loadingDialog" class="rv-header__title">{{ model.add.name }}</h1>
      <div class="rv-header__spacer"></div>

      <!-- Revert button (step 1 only) -->
      <span v-if="isFinanceArAdminRoles && !btnDisabled && e1 == 1">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <button
              class="rv-header__action rv-header__action--orange"
              :disabled="apiSubmit.isLoading"
              @click="revertCaseDialog = true"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon size="20" color="#ea580c">mdi-file-undo</v-icon>
            </button>
          </template>
          <span>Revert to taxPOD Admin</span>
        </v-tooltip>
      </span>

      <!-- Submit button (step 3 only) -->
      <span v-if="isFinanceArAdminRoles && !btnDisabled && e1 == 3">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <button
              class="rv-header__action rv-header__action--green"
              :disabled="apiSubmit.isLoading"
              @click="toggleSubmitDialog()"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon size="20" color="#16a34a">mdi-check-circle-outline</v-icon>
            </button>
          </template>
          <span>Submit</span>
        </v-tooltip>
      </span>
    </div>

    <!-- Step Indicator -->
    <div class="step-indicator">
      <div class="step-indicator__track">
        <!-- Step 1 -->
        <div class="step-indicator__step" :class="{ 'step-indicator__step--active': e1 === 1, 'step-indicator__step--done': e1 > 1 }">
          <div class="step-indicator__circle">
            <svg v-if="e1 > 1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor" class="step-indicator__check"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" /></svg>
            <span v-else>1</span>
          </div>
          <span class="step-indicator__label">Arrange Refund Sequence</span>
        </div>

        <!-- Line 1-2 -->
        <div class="step-indicator__line" :class="{ 'step-indicator__line--done': e1 > 1 }"></div>

        <!-- Step 2 -->
        <div class="step-indicator__step" :class="{ 'step-indicator__step--active': e1 === 2, 'step-indicator__step--done': e1 > 2 }">
          <div class="step-indicator__circle">
            <svg v-if="e1 > 2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor" class="step-indicator__check"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" /></svg>
            <span v-else>2</span>
          </div>
          <span class="step-indicator__label">Upload Attachments</span>
        </div>

        <!-- Line 2-3 -->
        <div class="step-indicator__line" :class="{ 'step-indicator__line--done': e1 > 2 }"></div>

        <!-- Step 3 -->
        <div class="step-indicator__step" :class="{ 'step-indicator__step--active': e1 === 3 }">
          <div class="step-indicator__circle">
            <span>3</span>
          </div>
          <span class="step-indicator__label">Preview Compiled PDF</span>
        </div>
      </div>
    </div>

    <!-- Step 1: Arrange Refund Sequence -->
    <div class="section-card" v-show="e1 === 1">
      <div class="section-header">
        <v-icon color="white" small>mdi-format-list-numbered</v-icon>
        <span class="section-header__title">Arrange Refund Sequence</span>
      </div>
      <div class="section-body">
        <!-- Company Name -->
        <div class="detail-row" @click="copyContent(formData.companyName)">
          <span class="detail-label">Company Name</span>
          <span class="detail-val">{{ formData.companyName }}
            <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
          </span>
        </div>

        <!-- Client/taxPOD Invoice No -->
        <div class="detail-row" @click="copyContent(formData.taxpodInvoiceNo)">
          <span class="detail-label">Client/taxPOD Invoice No.</span>
          <span class="detail-val">{{ formData.taxpodInvoiceNo }}
            <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
          </span>
        </div>

        <!-- HRDC Invoice No -->
        <div class="detail-row" @click="copyContent(formData.hrdcInvoiceNo)">
          <span class="detail-label">HRDC Invoice No.</span>
          <span class="detail-val">{{ formData.hrdcInvoiceNo }}
            <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
          </span>
        </div>

        <!-- Credit Note No -->
        <div class="detail-row" @click="copyContent(formData.creditNoteNo)">
          <span class="detail-label">Credit Note No.</span>
          <span class="detail-val">{{ formData.creditNoteNo }}
            <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
          </span>
        </div>

        <!-- Refund PDF Sequence -->
        <div class="detail-row detail-row--top">
          <span class="detail-label">Refund PDF Sequence</span>
          <div class="detail-field">
            <v-select
              v-model="formData.refundPdfSequence"
              :items="refundPdfSequence"
              filled
              label="Refund PDF Sequence"
              dense
              hide-details="auto"
              class="rv-input"
            ></v-select>
          </div>
        </div>

        <!-- Continue Button -->
        <div class="section-actions">
          <button
            class="rv-btn rv-btn--primary"
            @click="goto2ndStepper()"
            :disabled="!this.isFinanceArAdminRoles"
          >
            Continue
          </button>
        </div>
      </div>
    </div>

    <!-- Step 2: Upload Attachments -->
    <div class="section-card" v-show="e1 === 2">
      <div class="section-header">
        <v-icon color="white" small>mdi-paperclip</v-icon>
        <span class="section-header__title">Upload Attachments</span>
      </div>
      <div class="section-body">
        <!-- Bank Statement HRDF -->
        <div class="upload-group">
          <label class="upload-group__label">Bank Statement HRDF (CIMB)</label>
          <div
            class="file-card"
            :class="formData.bankStatementHrdc && formData.bankStatementHrdc.length > 0 ? 'file-card--has-file' : 'file-card--empty'"
            v-cloak
            @drop.prevent="addMultipleDropFile($event, 'bankStatementHrdc')"
            @dragover.prevent
          >
            <div class="file-card__icon-wrap">
              <div class="file-card__icon">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__svg">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                </svg>
              </div>
            </div>
            <div class="file-card__info">
              <span class="file-card__name">{{ formData.bankStatementHrdc && formData.bankStatementHrdc.length > 0 ? formData.bankStatementHrdc.map(f => f.name).join(', ') : 'No file selected' }}</span>
              <span class="file-card__hint">PDF only - drag & drop or click to upload</span>
            </div>
            <div class="file-card__actions">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <label class="file-card__btn" v-on="on">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__btn-icon"><path stroke-linecap="round" stroke-linejoin="round" d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13" /></svg>
                    <input type="file" class="file-card__hidden" accept=".pdf" multiple @change="handleMultiFileChange($event, 'bankStatementHrdc')" />
                  </label>
                </template>
                <span>Upload</span>
              </v-tooltip>
            </div>
          </div>
          <div v-if="errorField.bankStatementHrdc" class="rv-field-error">
            <ul v-if="Array.isArray(errorField.bankStatementHrdc)" class="rv-error-list"><li v-for="(msg, i) in errorField.bankStatementHrdc" :key="'bsH-'+i">{{ msg }}</li></ul>
            <span v-else>{{ errorField.bankStatementHrdc }}</span>
          </div>
        </div>

        <div class="upload-divider"></div>

        <!-- Bank Statement for Invoice Client (CIMB) -->
        <div
          class="upload-group"
          v-if="[REFUND_LIST_1_PAYMENT_VIA_CIMB].includes(formData.refundPdfSequence)"
        >
          <label class="upload-group__label">Bank Statement for Invoice Client</label>
          <div
            class="file-card"
            :class="formData.bankStatementForInvoiceClient && formData.bankStatementForInvoiceClient.length > 0 ? 'file-card--has-file' : 'file-card--empty'"
            v-cloak
            @drop.prevent="addMultipleDropFile($event, 'bankStatementForInvoiceClient')"
            @dragover.prevent
          >
            <div class="file-card__icon-wrap">
              <div class="file-card__icon">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__svg">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                </svg>
              </div>
            </div>
            <div class="file-card__info">
              <span class="file-card__name">{{ formData.bankStatementForInvoiceClient && formData.bankStatementForInvoiceClient.length > 0 ? formData.bankStatementForInvoiceClient.map(f => f.name).join(', ') : 'No file selected' }}</span>
              <span class="file-card__hint">PDF only - drag & drop or click to upload</span>
            </div>
            <div class="file-card__actions">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <label class="file-card__btn" v-on="on">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__btn-icon"><path stroke-linecap="round" stroke-linejoin="round" d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13" /></svg>
                    <input type="file" class="file-card__hidden" accept=".pdf" multiple @change="handleMultiFileChange($event, 'bankStatementForInvoiceClient')" />
                  </label>
                </template>
                <span>Upload</span>
              </v-tooltip>
            </div>
          </div>
          <div v-if="errorField.bankStatementForInvoiceClient" class="rv-field-error">
            <ul v-if="Array.isArray(errorField.bankStatementForInvoiceClient)" class="rv-error-list"><li v-for="(msg, i) in errorField.bankStatementForInvoiceClient" :key="'bsC-'+i">{{ msg }}</li></ul>
            <span v-else>{{ errorField.bankStatementForInvoiceClient }}</span>
          </div>
        </div>

        <!-- Stripe In -->
        <div
          class="upload-group"
          v-if="[REFUND_LIST_2_PAYMENT_VIA_STRIPE].includes(formData.refundPdfSequence)"
        >
          <label class="upload-group__label">Stripe In</label>
          <div
            class="file-card"
            :class="formData.stripeIn && formData.stripeIn.length > 0 ? 'file-card--has-file' : 'file-card--empty'"
            v-cloak
            @drop.prevent="addMultipleDropFile($event, 'stripeIn')"
            @dragover.prevent
          >
            <div class="file-card__icon-wrap">
              <div class="file-card__icon">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__svg">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                </svg>
              </div>
            </div>
            <div class="file-card__info">
              <span class="file-card__name">{{ formData.stripeIn && formData.stripeIn.length > 0 ? formData.stripeIn.map(f => f.name).join(', ') : 'No file selected' }}</span>
              <span class="file-card__hint">PDF only - drag & drop or click to upload</span>
            </div>
            <div class="file-card__actions">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <label class="file-card__btn" v-on="on">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__btn-icon"><path stroke-linecap="round" stroke-linejoin="round" d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13" /></svg>
                    <input type="file" class="file-card__hidden" accept=".pdf" multiple @change="handleMultiFileChange($event, 'stripeIn')" />
                  </label>
                </template>
                <span>Upload</span>
              </v-tooltip>
            </div>
          </div>
          <div v-if="errorField.stripeIn" class="rv-field-error">
            <ul v-if="Array.isArray(errorField.stripeIn)" class="rv-error-list"><li v-for="(msg, i) in errorField.stripeIn" :key="'sI-'+i">{{ msg }}</li></ul>
            <span v-else>{{ errorField.stripeIn }}</span>
          </div>
        </div>

        <!-- Stripe Payout -->
        <div
          class="upload-group"
          v-if="[REFUND_LIST_2_PAYMENT_VIA_STRIPE].includes(formData.refundPdfSequence)"
        >
          <label class="upload-group__label">Stripe Payout</label>
          <div
            class="file-card"
            :class="formData.stripePayout && formData.stripePayout.length > 0 ? 'file-card--has-file' : 'file-card--empty'"
            v-cloak
            @drop.prevent="addMultipleDropFile($event, 'stripePayout')"
            @dragover.prevent
          >
            <div class="file-card__icon-wrap">
              <div class="file-card__icon">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__svg">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                </svg>
              </div>
            </div>
            <div class="file-card__info">
              <span class="file-card__name">{{ formData.stripePayout && formData.stripePayout.length > 0 ? formData.stripePayout.map(f => f.name).join(', ') : 'No file selected' }}</span>
              <span class="file-card__hint">PDF only - drag & drop or click to upload</span>
            </div>
            <div class="file-card__actions">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <label class="file-card__btn" v-on="on">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__btn-icon"><path stroke-linecap="round" stroke-linejoin="round" d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13" /></svg>
                    <input type="file" class="file-card__hidden" accept=".pdf" multiple @change="handleMultiFileChange($event, 'stripePayout')" />
                  </label>
                </template>
                <span>Upload</span>
              </v-tooltip>
            </div>
          </div>
          <div v-if="errorField.stripePayout" class="rv-field-error">
            <ul v-if="Array.isArray(errorField.stripePayout)" class="rv-error-list"><li v-for="(msg, i) in errorField.stripePayout" :key="'sP-'+i">{{ msg }}</li></ul>
            <span v-else>{{ errorField.stripePayout }}</span>
          </div>
        </div>

        <!-- iPay88 -->
        <div
          class="upload-group"
          v-if="[REFUND_LIST_3_PAYMENT_VIA_IPAY88].includes(formData.refundPdfSequence)"
        >
          <label class="upload-group__label">iPay88</label>
          <div
            class="file-card"
            :class="formData.iPay88 && formData.iPay88.length > 0 ? 'file-card--has-file' : 'file-card--empty'"
            v-cloak
            @drop.prevent="addMultipleDropFile($event, 'iPay88')"
            @dragover.prevent
          >
            <div class="file-card__icon-wrap">
              <div class="file-card__icon">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__svg">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                </svg>
              </div>
            </div>
            <div class="file-card__info">
              <span class="file-card__name">{{ formData.iPay88 && formData.iPay88.length > 0 ? formData.iPay88.map(f => f.name).join(', ') : 'No file selected' }}</span>
              <span class="file-card__hint">PDF only - drag & drop or click to upload</span>
            </div>
            <div class="file-card__actions">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <label class="file-card__btn" v-on="on">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__btn-icon"><path stroke-linecap="round" stroke-linejoin="round" d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13" /></svg>
                    <input type="file" class="file-card__hidden" accept=".pdf" multiple @change="handleMultiFileChange($event, 'iPay88')" />
                  </label>
                </template>
                <span>Upload</span>
              </v-tooltip>
            </div>
          </div>
          <div v-if="errorField.iPay88" class="rv-field-error">
            <ul v-if="Array.isArray(errorField.iPay88)" class="rv-error-list"><li v-for="(msg, i) in errorField.iPay88" :key="'ip-'+i">{{ msg }}</li></ul>
            <span v-else>{{ errorField.iPay88 }}</span>
          </div>
        </div>

        <!-- iPay88 Payout -->
        <div
          class="upload-group"
          v-if="[REFUND_LIST_3_PAYMENT_VIA_IPAY88].includes(formData.refundPdfSequence)"
        >
          <label class="upload-group__label">iPay88 Payout</label>
          <div
            class="file-card"
            :class="formData.iPay88Payout && formData.iPay88Payout.length > 0 ? 'file-card--has-file' : 'file-card--empty'"
            v-cloak
            @drop.prevent="addMultipleDropFile($event, 'iPay88Payout')"
            @dragover.prevent
          >
            <div class="file-card__icon-wrap">
              <div class="file-card__icon">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__svg">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                </svg>
              </div>
            </div>
            <div class="file-card__info">
              <span class="file-card__name">{{ formData.iPay88Payout && formData.iPay88Payout.length > 0 ? formData.iPay88Payout.map(f => f.name).join(', ') : 'No file selected' }}</span>
              <span class="file-card__hint">PDF only - drag & drop or click to upload</span>
            </div>
            <div class="file-card__actions">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <label class="file-card__btn" v-on="on">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__btn-icon"><path stroke-linecap="round" stroke-linejoin="round" d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13" /></svg>
                    <input type="file" class="file-card__hidden" accept=".pdf" multiple @change="handleMultiFileChange($event, 'iPay88Payout')" />
                  </label>
                </template>
                <span>Upload</span>
              </v-tooltip>
            </div>
          </div>
          <div v-if="errorField.iPay88Payout" class="rv-field-error">
            <ul v-if="Array.isArray(errorField.iPay88Payout)" class="rv-error-list"><li v-for="(msg, i) in errorField.iPay88Payout" :key="'ipP-'+i">{{ msg }}</li></ul>
            <span v-else>{{ errorField.iPay88Payout }}</span>
          </div>
        </div>

        <!-- Bank Statement CIMB (Payout) -->
        <div
          class="upload-group"
          v-if="[REFUND_LIST_2_PAYMENT_VIA_STRIPE, REFUND_LIST_3_PAYMENT_VIA_IPAY88].includes(formData.refundPdfSequence)"
        >
          <label class="upload-group__label">Bank Statement CIMB Payout</label>
          <div
            class="file-card"
            :class="formData.bankStatementCimbPayout && formData.bankStatementCimbPayout.length > 0 ? 'file-card--has-file' : 'file-card--empty'"
            v-cloak
            @drop.prevent="addMultipleDropFile($event, 'bankStatementCimbPayout')"
            @dragover.prevent
          >
            <div class="file-card__icon-wrap">
              <div class="file-card__icon">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__svg">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                </svg>
              </div>
            </div>
            <div class="file-card__info">
              <span class="file-card__name">{{ formData.bankStatementCimbPayout && formData.bankStatementCimbPayout.length > 0 ? formData.bankStatementCimbPayout.map(f => f.name).join(', ') : 'No file selected' }}</span>
              <span class="file-card__hint">PDF only - drag & drop or click to upload</span>
            </div>
            <div class="file-card__actions">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <label class="file-card__btn" v-on="on">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__btn-icon"><path stroke-linecap="round" stroke-linejoin="round" d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13" /></svg>
                    <input type="file" class="file-card__hidden" accept=".pdf" multiple @change="handleMultiFileChange($event, 'bankStatementCimbPayout')" />
                  </label>
                </template>
                <span>Upload</span>
              </v-tooltip>
            </div>
          </div>
          <div v-if="errorField.bankStatementCimbPayout" class="rv-field-error">
            <ul v-if="Array.isArray(errorField.bankStatementCimbPayout)" class="rv-error-list"><li v-for="(msg, i) in errorField.bankStatementCimbPayout" :key="'bsCP-'+i">{{ msg }}</li></ul>
            <span v-else>{{ errorField.bankStatementCimbPayout }}</span>
          </div>
        </div>

        <!-- Step 2 Actions -->
        <div class="section-actions">
          <button class="rv-btn rv-btn--secondary" @click="e1 = 1">
            Back
          </button>
          <button
            class="rv-btn rv-btn--primary"
            @click="goto3rdStepper()"
            :disabled="!this.isFinanceArAdminRoles"
          >
            Continue
          </button>
        </div>
      </div>
    </div>

    <!-- Step 3: Preview Compiled PDF -->
    <div class="section-card" v-show="e1 === 3">
      <div class="section-header section-header--green">
        <v-icon color="white" small>mdi-file-eye</v-icon>
        <span class="section-header__title">Preview Compiled PDF</span>
      </div>
      <div class="section-body">
        <div class="upload-group">
          <label class="upload-group__label">Compiled PDF Attachment</label>
          <div
            class="file-card"
            :class="formData.compiledPdf ? 'file-card--has-file' : 'file-card--empty'"
            v-cloak
            @drop.prevent="addSingleDropFile($event, 'compiledPdf')"
            @dragover.prevent
          >
            <div class="file-card__icon-wrap">
              <div class="file-card__icon">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__svg">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                </svg>
              </div>
            </div>
            <div class="file-card__info">
              <span class="file-card__name">{{ formData.compiledPdf ? formData.compiledPdf.name : 'No file selected' }}</span>
              <span class="file-card__hint">PDF only - drag & drop or click to upload</span>
            </div>
            <div class="file-card__actions">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <label class="file-card__btn" v-on="on">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__btn-icon"><path stroke-linecap="round" stroke-linejoin="round" d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13" /></svg>
                    <input type="file" class="file-card__hidden" accept=".pdf" @change="handleSingleFileChange($event, 'compiledPdf')" />
                  </label>
                </template>
                <span>Upload</span>
              </v-tooltip>
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <button class="file-card__btn" @click="previewCompiledPdf('compiledPdf')" v-on="on">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__btn-icon"><path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" /><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /></svg>
                  </button>
                </template>
                <span>Preview</span>
              </v-tooltip>
            </div>
          </div>
          <div v-if="errorField.compiledPdf" class="rv-field-error">
            <ul v-if="Array.isArray(errorField.compiledPdf)" class="rv-error-list"><li v-for="(msg, i) in errorField.compiledPdf" :key="'cp-'+i">{{ msg }}</li></ul>
            <span v-else>{{ errorField.compiledPdf }}</span>
          </div>
        </div>

        <!-- Step 3 Actions -->
        <div class="section-actions">
          <button class="rv-btn rv-btn--secondary" @click="e1 = 2">
            Back
          </button>
        </div>
      </div>
    </div>

    <HrdcLoader :loadingDialog="loadingDialog"></HrdcLoader>
  </div>
</template>

<script>
import moment from "moment";
import { mapState } from "vuex";
import Api from "@/objects/api";
import Model from "@/objects/model";
import Service from "@/objects/service";
import hrdcFunction from "@/objects/hrdcFunction";
import HrdcData from "@/services/hrdc/objects/globalData";
import TheModalConfirmation from "@/components/common/TheModalConfirmation.vue";

export default {
  mixins: [hrdcFunction],
  components: {
    TheModalConfirmation,
  },
  computed: {
    ...mapState({
      auth: (state) => state.auth.data,
    }),
  },
  props: ["params"],
  data: () => ({
    apiGetApplicationSingleDetail: new Api(),
    apiCompilePdf: new Api(),
    apiValidateAttachmentAndCompile: new Api(),
    // apiGetArToApEmailDetails: new Api(),
    apiRejectApplicationToTaxpodAdmin: new Api(),
    apiSubmit: new Api(),
    model: new Model(),
    service: new Service(),
    hrdcData: new HrdcData(),
    alertError: [],
    date: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .substr(0, 10),
    menu: false,
    formData: {
      applicationNo: null,
      refundEmailArToApDate: new Date(
        Date.now() - new Date().getTimezoneOffset() * 60000
      )
        .toISOString()
        .substr(0, 10),
      // officialReceiptHrdc: [],
      // officialReceiptAndSOA: [],
      bankStatementHrdc: [],
      bankStatementForInvoiceClient: [],
      refundPdfSequence: 1,
      compiledPdf: null,
      compiledPdfName: null,
      stripeIn: [],
      stripePayout: [],
      iPay88: [],
      iPay88Payout: [],
      eGHL: [],
      bankStatementCimbPayout: [],
      // toEmail: [],
      // ccEmail: [],
      // emailContent: null,
      hrdcInvoiceNo: null,
      creditNoteNo: null,
      taxpodInvoiceNo: null,
      companyName: null,
    },
    errorField: {
      applicationNo: null,
      refundEmailArToApDate: null,
      // officialReceiptHrdc: null,
      // officialReceiptAndSOA: null,
      bankStatementHrdc: null,
      bankStatementForInvoiceClient: null,
      stripeIn: null,
      stripePayout: null,
      iPay88: null,
      iPay88Payout: null,
      eGHL: null,
      bankStatementCimbPayout: null,
      // toEmail: null,
      // ccEmail: null,
      // emailContent: null,
      compiledPdf: null,
      rejectReason: null,
    },
    loadingDialog: false,
    refundEmailArToApSubmitDialog: false,
    revertCaseDialog: false,
    rejectReason: null,
    editMode: false,
    btnDisabled: false,
    e1: 1,
    refundPdfSequence: [
      {
        text: "Refund List 1 - Default (CIMB)",
        value: 1,
      },
      {
        text: "Refund List 2 - Payment via Stripe",
        value: 2,
      },
      {
        text: "Refund List 3 - Payment via iPay88",
        value: 3,
      },
    ],
    REFUND_LIST_1_PAYMENT_VIA_CIMB: 1,
    REFUND_LIST_2_PAYMENT_VIA_STRIPE: 2,
    REFUND_LIST_3_PAYMENT_VIA_IPAY88: 3,
    // Statc Attachments is all 4 sequence must include.
    staticAttachments: [
      // "officialReceiptHrdc",
      // "officialReceiptAndSOA",
      "bankStatementHrdc",
    ],
    // Dynamic Attachments is depends on sequence.
    dynamicAttachments: [
      "bankStatementForInvoiceClient",
      "stripeIn",
      "stripePayout",
      "bankStatementCimbPayout",
      "iPay88",
      "iPay88Payout",
      "eGHL",
    ],
  }),
  async created() {
    this.loadingDialog = !this.loadingDialog;
    this.formData.applicationNo = this.$route.query._ausk;
    this.model.getByKey(this.params.modelKey);
    if (this.model.serviceKey) this.service.set("key", this.model.serviceKey);

    // Assign apiDetails by mapping repository.
    const apiDetails = {
      apiSubmit: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/refund_email_ar_to_ap/create-refund-email-ar-to-ap`,
        method: "post",
      },
      apiGetApplicationSingleDetail: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/application/get-single-application`,
        method: "post",
        params: {
          applicationUuid: this.$route.query._ausk,
        },
      },
      apiCompilePdf: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/refund_email_ar_to_ap/compile-pdf`,
        method: "post",
      },
      apiValidateAttachmentAndCompile: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/refund_email_ar_to_ap/validate-attachment-and-compile`,
        method: "post",
      },
      apiRejectApplicationToTaxpodAdmin: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/refund_email_ar_to_ap/reject-application-to-taxpod-admin`,
        method: "post",
      },
    };

    for (const api in apiDetails) {
      if (apiDetails[api].url !== undefined) {
        this[api].setUrl(apiDetails[api].url);
      }
      if (apiDetails[api].method !== undefined) {
        this[api].setMethod(apiDetails[api].method);
      }
      if (apiDetails[api].params !== undefined) {
        this[api].setParams(apiDetails[api].params);
      }
    }

    this.stages = await this.hrdcData.allStages;
    const currentStageId =
      this.hrdcData.stageIndex[this.$store.state.hrdc.data.stageId];
    this.rolesAssignation(this.auth.roles);
    if (!this.isFinanceArAdminRoles) {
      this.alertError.push("You are not allowed to perform this action!");
      this.btnDisabled = true;
    }

    this.checkFormAlreadyFilledGreater(
      currentStageId,
      this.hrdcData.stageSlug.refundEmailApToCimbRejected
    );

    this.checkFormNotYetFilledLesser(
      currentStageId,
      this.hrdcData.stageSlug.refundEmailApproval2
    );

    this.apiGetApplicationSingleDetail.setCallbackCompleted((response) => {
      try {
        const { status, data } = response;
        if (status) {
          this.formData.companyName = data.oriCompanyName;
          this.formData.hrdcInvoiceNo = data.manualInvoiceToHrdc;
          this.formData.creditNoteNo = data.creditNoteNo;
          this.formData.taxpodInvoiceNo = data.autoGeneratedTaxpodInvoiceNo;

          const companyName = data.oriCompanyName.toUpperCase();
          const claimAmount = `RM${this.formatNumber(data.totalPrice)}`;
          this.formData.compiledPdfName = `COMPILED PDF - ${companyName} - REFUND_${claimAmount}.pdf`;

          const refundEmailArToApStageId =
            this.hrdcData.stagePriority.refundEmailArToAp;

          const refundArToApObj = data.ApplicationLog.find(
            (obj) => obj.stageId == refundEmailArToApStageId
          );

          if (refundArToApObj) {
            this.formData.refundEmailArToApDate = moment(
              refundArToApObj.startDate
            ).format("YYYY-MM-DD");
          }
        }

        this.loadingDialog = !this.loadingDialog;
      } catch (err) {
        console.log(err);
      }
    });
    this.apiGetApplicationSingleDetail.fetch();
  },
  methods: {
    clearErrorMsg(field = null) {
      if (!field) {
        for (let error in this.errorField) {
          this.errorField[error] = null;
        }
      } else {
        this.errorField[field] = null;
      }
    },
    clearAttachments(attachments) {
      for (const atch of attachments) {
        this.formData[atch] = [];
      }
    },
    showErrorMessage(response) {
      for (const key in response.data) {
        this.errorField[key] = null;
        this.errorField[key] = response.data[key];
      }
    },
    //EOC
    modalRefundEmailArToApSubmit() {
      this.refundEmailArToApSubmitDialog = !this.refundEmailArToApSubmitDialog;
      this.submit();
    },
    toggleSubmitDialog() {
      this.$refs.confirmDialog.open({
        title: this.model.name.singular,
        message:
          "Please confirm that you wish to record Refund Email AR to AP date.",
        options: {
          btnDisagree: "Cancel",
          btnAgree: "Submit",
        },
        agree: () => {
          this.submit();
        },
        disagree: () => {},
      });
    },
    redirectBack() {
      let route = "HrdcApplication";
      if (this.params.backTo) {
        route = this.params.backTo;
      }

      this.$store.commit("assignDeveloperData", {
        paginationPageNo: this.$route.query.currentPageNo,
      });

      this.$router.push({
        name: route,
        params: {
          modelKey: this.model.key,
          backTo: "",
        },
        query: {
          viewId: this.$store.state.hrdc.data.viewId,
          _ausk: this.$route.query._ausk,
        },
      });
    },
    goto2ndStepper() {
      this.e1 = 2;

      // Clear all previous error messages.
      this.clearErrorMsg();

      // Clear all dynamic attachments.
      this.clearAttachments(this.dynamicAttachments);
    },
    goto3rdStepper() {
      this.loadingDialog = !this.loadingDialog;

      const fd = new FormData();
      fd.append("applicationUuid", this.$route.query._ausk);
      fd.append("refundPdfSequence", this.formData.refundPdfSequence);

      for (const sAtch of this.staticAttachments) {
        for (let i = 0; i < this.formData[sAtch].length; i++) {
          fd.append(sAtch, this.formData[sAtch][i]);
        }
      }

      for (const dAtch of this.dynamicAttachments) {
        for (let j = 0; j < this.formData[dAtch].length; j++) {
          fd.append(dAtch, this.formData[dAtch][j]);
        }
      }

      this.apiValidateAttachmentAndCompile.setParams(fd);
      this.apiValidateAttachmentAndCompile.setCallbackCompleted((response) => {
        try {
          const { status, data } = response;
          this.clearErrorMsg();
          if (!status) {
            this.showErrorMessage(response);
            return;
          }

          if (status == "No_APPROVAL_FOUND") {
            this.$store.dispatch("showMessage", {
              message: data,
              messageType: "error",
            });
            return;
          }

          if (status == "FAILED_COMPILE_PDF") {
            this.alertError.push(
              `Failed to compile attachment, Please screenshot the error message for administrator: ${data}`
            );
            return;
          }

          if (status) {
            try {
              const blob = new Blob([new Uint8Array(data)]);
              this.formData.compiledPdf = new File(
                [blob],
                this.formData.compiledPdfName,
                {
                  type: "application/pdf",
                }
              );
            } catch (err) {
              console.log(err);
            }
            this.e1 = 3;
          }
        } catch (err) {
          console.log(err);
        } finally {
          this.loadingDialog = !this.loadingDialog;
        }
      });
      this.apiValidateAttachmentAndCompile.fetch();
    },
    formatNumber(number) {
      if (number) {
        return Number(number).toLocaleString();
      }
    },
    emailSignatureName() {
      let signature = this.auth.name.split(" ").slice(0, 2).join(" ");
      if (this.auth.signatureName) {
        signature = this.auth.signatureName;
      }
      return signature;
    },
    handleMultiFileChange(e, key) {
      const files = e.target.files;
      for (const file of files) {
        if (!file.type.match("pdf.*")) {
          this.errorField[key] = "Please select pdf file format";
          return;
        }
        this.formData[key].push(file);
      }
      this.clearErrorMsg(key);
    },
    handleSingleFileChange(e, key) {
      const file = e.target.files[0];
      if (!file.type.match("pdf.*")) {
        this.errorField[key] = "Please select pdf file format";
        return;
      }
      this.formData[key] = file;
      this.clearErrorMsg(key);
    },
    addMultipleDropFile(e, key) {
      let file = e.dataTransfer.files;

      for (const fileItem of file) {
        if (!fileItem.type.match("pdf.*")) {
          this.errorField[key] = "Please select pdf file format";
          return;
        }

        this.formData[key].push(fileItem);
      }

      if (e) {
        this.clearErrorMsg(key);
      }
    },
    addSingleDropFile(e, key) {
      let file = e.dataTransfer.files[0];

      if (!file.type.match("pdf.*")) {
        this.errorField[key] = "Please select pdf file format";
        return;
      }

      this.formData[key] = file;

      if (e) {
        this.clearErrorMsg(key);
      }
    },
    copyContent(item) {
      const textarea = document.createElement("textarea");
      textarea.value = item;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");

      // Remove the temporary textarea
      document.body.removeChild(textarea);

      this.$store.dispatch("showMessage", {
        message: "Copied Successfully!",
        messageType: "info",
      });
    },
    previewCompiledPdf(fileType) {
      if (!this.formData[fileType]) {
        this.$store.dispatch("showMessage", {
          message: "No Preview Available",
          messageType: "info",
        });
        return;
      }

      let url = null;
      let newWindow = null;
      switch (fileType) {
        default:
          url = URL.createObjectURL(this.formData[fileType]);
          newWindow = window.open(url, "_blank");
          break;
      }

      if (newWindow) {
        newWindow.onload = function () {
          // Revoke the object URL when the window is closed
          newWindow.addEventListener("beforeunload", function () {
            URL.revokeObjectURL(url);
          });
        };
      }
    },
    revertCase() {
      this.showLoadingDialog();

      this.clearErrorMsg();

      this.apiRejectApplicationToTaxpodAdmin.setParams({
        userUuid: this.auth.uuid,
        revertBackToStageId: this.hrdcData.stagePriority.refundEmailApproval1,
        refundEmailArToApRejectedStageId:
          this.hrdcData.stagePriority.refundEmailArToApRejected,
        applicationUuid: this.$route.query._ausk,
        rejectReason: this.rejectReason,
      });
      this.apiRejectApplicationToTaxpodAdmin.setCallbackCompleted(
        (response) => {
          try {
            if (!response.status) {
              this.showErrorMessage(response);
              this.hideLoadingDialog();
            }

            if (response.status) {
              this.revertCaseDialog = false;
              this.rejectReason = null;

              this.$store.dispatch("showMessage", {
                message: response.data,
                messageType: "success",
                timeout: 2000,
              });

              this.$router.push({
                name: "HrdcApplication",
                params: {
                  modelKey: this.model.key,
                  backTo: "",
                },
                query: {
                  viewId: this.$store.state.hrdc.data.viewId,
                  _ausk: this.$route.query._ausk,
                },
              });

              this.hideLoadingDialog();
            }
          } catch (err) {
            console.log(err);
            this.alertError.push(err.message);
            return false;
          }
        }
      );
      this.apiRejectApplicationToTaxpodAdmin.fetch();
    },
    async submit() {
      this.loadingDialog = !this.loadingDialog;

      const fd = new FormData();
      fd.append("applicationUuid", this.formData.applicationNo);
      fd.append("referById", this.auth.uuid);
      fd.append("editMode", this.editMode);
      fd.append("compiledPdf", this.formData.compiledPdf);
      // fd.append("toEmail", this.formData.toEmail);
      // fd.append("ccEmail", this.formData.ccEmail);
      // fd.append("emailContent", this.formData.emailContent);
      fd.append("refundEmailArToApDate", this.formData.refundEmailArToApDate);

      this.apiSubmit.setParams(fd);
      this.apiSubmit.setCallbackCompleted((response) => {
        try {
          this.clearErrorMsg();
          this.loadingDialog = !this.loadingDialog;
          if (!response.status) {
            this.showErrorMessage(response);
            if (response.message) {
              this.$store.dispatch("showMessage", {
                message: response.message,
                messageType: "error",
                timeout: 2000,
              });
            }
          }

          if (response.status) {
            this.$store.commit("assignDeveloperData", {
              paginationPageNo: this.$route.query.currentPageNo,
            });

            this.$router.push({
              name: "HrdcApplication",
              query: {
                viewId: this.$store.state.hrdc.data.viewId,
              },
            });
            this.$store.dispatch("showMessage", {
              message: response.data,
              messageType: "success",
              timeout: 2000,
            });
          }
        } catch (err) {
          console.log(err);
        }
      });
      this.apiSubmit.fetch();
    },
  },
};
</script>

<style>
/* The unscoped `.v-menu__content …` block that used to sit here is gone.
   It was global — unscoped CSS is injected once and never removed, so a rule
   written for THIS page's dropdown repainted every menu in the app (with
   !important, and with hard-coded light hex) for the rest of the session. That
   is what made the same v-select look different depending on which page you had
   visited. Menus are defined ONCE, in tokens, in src/plugins/theme-glass.css §3
   — panel, list, item radius, hover, active, typography and the state overlay.
   A page that genuinely needs its own menu styling should give that v-menu a
   `content-class` and key off it, the way .bdt-menu and .tb-view-menu do. */

/* >>> end auto-dark <<< */

/* ============================================
   REVERT DIALOG (unscoped — for v-dialog overlay)
   ============================================ */
.rv-revert-dialog {
  border-radius: 20px !important;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12) !important;
}

</style>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .rv-page {
  background-color: var(--app-page);
}

.dark-mode .rv-revert-card {
  background: var(--app-form-tile);
}

.dark-mode .rv-revert-card__icon-wrap {
  background: var(--app-warning-bg);
}

.dark-mode .rv-revert-card__title {
  color: var(--app-text);
}

.dark-mode .rv-revert-card__label {
  color: var(--app-text);
}

.dark-mode .rv-revert-card__btn--cancel {
  background: var(--app-form-tile);
  border: 1.5px solid var(--app-border-strong);
}

.dark-mode .rv-revert-card__btn--cancel:hover {
  background: var(--app-form-tile-hover);
}

.dark-mode .rv-header {
  background: var(--app-elevated);
}

.dark-mode .rv-header__back {
  background: var(--app-form-tile);
  border: 1px solid var(--app-border);
}

.dark-mode .rv-header__back:hover {
  background: var(--app-form-tile-hover);
  border-color: var(--app-border-strong);
}

.dark-mode .rv-header__title {
  color: var(--app-text);
}

.dark-mode .rv-header__action {
  background: var(--app-form-tile);
  border: 1px solid var(--app-border);
}

.dark-mode .rv-header__action:hover {
  background: var(--app-accent-soft);
}

.dark-mode .rv-header__action--orange:hover {
  background: var(--app-orange-bg);
}

.dark-mode .rv-header__action--green:hover {
  background: var(--app-success-bg);
}

.dark-mode .step-indicator {
  background: var(--app-form-tile);
}

.dark-mode .step-indicator__circle {
  background: var(--app-form-tile);
  border: 2px solid var(--app-border-strong);
  color: var(--app-text-faint);
}

.dark-mode .step-indicator__label {
  color: var(--app-text-faint);
}

.dark-mode .step-indicator__line {
  background: var(--app-form-tile);
}

.dark-mode .section-card {
  background: var(--app-elevated);
}

.dark-mode .section-body {
  background: var(--app-card);
}

.dark-mode .detail-val {
  border: 1px solid var(--app-border);
}

.dark-mode .upload-group__label {
  color: var(--app-text);
}

.dark-mode .upload-divider {
  background: var(--app-form-tile);
}

.dark-mode .file-card {
  background: var(--app-surface);
}

.dark-mode .file-card:hover {
  background: var(--app-form-tile-hover);
}

.dark-mode .file-card__icon {
  background: var(--app-danger-bg);
}

.dark-mode .file-card__icon-wrap::after {
  border: 2px solid var(--app-border);
}

.dark-mode .file-card--has-file .file-card__icon-wrap::after {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke-width='3' stroke='var(--app-card)'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='m4.5 12.75 6 6 9-13.5'/%3E%3C/svg%3E");
}

.dark-mode .file-card--empty .file-card__icon-wrap::after {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke-width='3' stroke='var(--app-card)'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M6 18 18 6M6 6l12 12'/%3E%3C/svg%3E");
}

.dark-mode .file-card__name {
  color: var(--app-text);
}

.dark-mode .file-card__hint {
  color: var(--app-text-faint);
}

.dark-mode .file-card__btn {
  background: var(--app-form-tile);
  color: var(--app-text-muted);
}

.dark-mode .file-card__btn:hover {
  background: var(--app-form-tile-hover);
  color: var(--app-text);
}

.dark-mode .rv-btn--secondary {
  background: var(--app-form-tile);
  color: var(--app-text-muted);
  border: 1.5px solid var(--app-border-strong);
}

.dark-mode .rv-btn--secondary:hover {
  background: var(--app-form-tile-hover);
  border-color: var(--app-border-strong);
}

.dark-mode .rv-input >>> .v-input__slot {
  background: var(--app-field) !important;
  border: 1px solid var(--app-field-border) !important;
}

.dark-mode .rv-input >>> .v-label {
  color: var(--app-field-placeholder) !important;
}

/* >>> end auto-dark <<< */

/* ============================================
   PAGE WRAPPER
   ============================================ */
.rv-page {
  background-color: #d8dce2;
  /* Fills main minus the breadcrumb and footer; a `100vh - 64px` guess only
     subtracts the app bar and overflows. See .layout-main__content. */
  flex: 1 0 auto;
  padding: 24px 24px 48px;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ============================================
   REVERT DIALOG CARD
   ============================================ */
.rv-revert-card {
  background: #fff;
  border-radius: 20px;
  padding: 0;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
}

.rv-revert-card__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 24px 28px 0;
}

.rv-revert-card__icon-wrap {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: #fef3c7;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.rv-revert-card__title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.rv-revert-card__body {
  padding: 20px 28px;
}

.rv-revert-card__label {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: #334155;
  margin-bottom: 10px;
  line-height: 1.5;
}

.rv-revert-card__actions {
  display: flex;
  gap: 12px;
  padding: 0 28px 24px;
}

.rv-revert-card__btn {
  flex: 1;
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: 'Lato', sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;
}

.rv-revert-card__btn--cancel {
  background: #fff;
  color: #0ea5e9;
  border: 1.5px solid #d1d5db;
}

.rv-revert-card__btn--cancel:hover {
  background: #f8fafc;
  border-color: #0ea5e9;
}

.rv-revert-card__btn--confirm {
  background: #56bde8;
  color: #fff;
  border: none;
}

.rv-revert-card__btn--confirm:hover {
  background: #3dadd8;
  box-shadow: 0 4px 12px rgba(86, 189, 232, 0.3);
}

/* ============================================
   HEADER CARD
   ============================================ */
.rv-header {
  display: flex;
  align-items: center;
  gap: 14px;
  background: #eaecef;
  border-radius: 16px;
  padding: 14px 20px;
  margin-bottom: 20px;
}

.rv-header__back {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #e2e4e8;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s;
  flex-shrink: 0;
}

.rv-header__back:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.rv-header__title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.rv-header__spacer {
  flex: 1;
}

.rv-header__action {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #e2e4e8;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s;
  flex-shrink: 0;
}

.rv-header__action:hover {
  background: #f0f9ff;
  border-color: #0ea5e9;
}

.rv-header__action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.rv-header__action--orange:hover {
  background: #fff7ed;
  border-color: #f97316;
}

.rv-header__action--green:hover {
  background: #f0fdf4;
  border-color: #22c55e;
}

/* ============================================
   STEP INDICATOR
   ============================================ */
.step-indicator {
  background: #eaecef;
  border-radius: 14px;
  padding: 28px 40px;
  margin-bottom: 20px;
}

.step-indicator__track {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 0;
}

.step-indicator__step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
  min-width: 140px;
}

.step-indicator__circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid #cbd5e1;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 700;
  transition: all 0.3s ease;
  position: relative;
  z-index: 2;
}

.step-indicator__step--active .step-indicator__circle {
  background: #0ea5e9;
  border-color: #0ea5e9;
  color: #fff;
  box-shadow: 0 4px 14px rgba(14, 165, 233, 0.35);
}

.step-indicator__step--done .step-indicator__circle {
  background: #22c55e;
  border-color: #22c55e;
  color: #fff;
}

.step-indicator__check {
  width: 18px;
  height: 18px;
}

.step-indicator__label {
  font-size: 0.72rem;
  font-weight: 600;
  color: #94a3b8;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1.3;
  max-width: 120px;
}

.step-indicator__step--active .step-indicator__label {
  color: #0ea5e9;
}

.step-indicator__step--done .step-indicator__label {
  color: #22c55e;
}

.step-indicator__line {
  flex: 1;
  height: 2px;
  background: #cbd5e1;
  margin-top: 20px;
  min-width: 40px;
  transition: background 0.3s ease;
}

.step-indicator__line--done {
  background: #22c55e;
}

/* ============================================
   SECTION CARD
   ============================================ */
.section-card {
  background: #eaecef;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  overflow: hidden;
  margin-bottom: 20px;
  transition: box-shadow 0.3s ease;
}

.section-card:hover {
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.06);
}

.section-header {
  background: linear-gradient(135deg, #0ea5e9, #38bdf8);
  padding: 13px 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.section-header--green {
  background: linear-gradient(135deg, #10b981, #34d399);
}

.section-header__title {
  color: #ffffff;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
}

.section-body {
  padding: 22px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ============================================
   DETAIL ROW (read-only display)
   ============================================ */
.detail-row {
  display: flex;
  align-items: center;
  padding: 6px 0;
  cursor: pointer;
}

.detail-row--top {
  align-items: flex-start;
}

.detail-label {
  flex: 0 0 200px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #2d3239;
  line-height: 1.6;
  padding-top: 1px;
}

.detail-val {
  flex: 1;
  font-size: 0.85rem;
  color: #1a1e24;
  line-height: 1.5;
  word-break: break-word;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  background: #f1f3f5;
  border: 1px solid #e2e4e8;
  border-radius: 8px;
  padding: 7px 14px;
}

.copy-icon {
  opacity: 0;
  transition: opacity 0.2s ease;
  margin-left: 8px;
  color: #a0aab4 !important;
}

.detail-row:hover .copy-icon {
  opacity: 1;
}

.detail-field {
  flex: 1;
}

/* ============================================
   UPLOAD GROUP
   ============================================ */
.upload-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.upload-group__label {
  font-size: 0.8rem;
  font-weight: 700;
  color: #334155;
}

.upload-divider {
  height: 1px;
  background: #e2e8f0;
  margin: 4px 0;
}

/* ============================================
   FILE CARD
   ============================================ */
.file-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 12px;
  background: #f8fafc;
  transition: background 0.15s;
}

.file-card:hover {
  background: #f1f5f9;
}

.file-card__icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #fef2f2;
  color: #ef4444;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.file-card__icon-wrap {
  position: relative;
  flex-shrink: 0;
}

.file-card__icon-wrap::after {
  content: '';
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid #f8fafc;
  background-size: 10px;
  background-position: center;
  background-repeat: no-repeat;
}

.file-card--has-file .file-card__icon-wrap::after {
  background-color: #22c55e;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke-width='3' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='m4.5 12.75 6 6 9-13.5'/%3E%3C/svg%3E");
}

.file-card--empty .file-card__icon-wrap::after {
  background-color: #ef4444;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke-width='3' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M6 18 18 6M6 6l12 12'/%3E%3C/svg%3E");
}

.file-card__svg {
  width: 20px;
  height: 20px;
}

.file-card__info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.file-card__name {
  font-size: 0.82rem;
  font-weight: 500;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-card__hint {
  font-size: 0.7rem;
  color: #94a3b8;
}

.file-card__actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.file-card__btn {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}

.file-card__btn:hover {
  background: #e2e8f0;
  color: #334155;
}

.file-card__btn-icon {
  width: 16px;
  height: 16px;
}

.file-card__hidden {
  display: none;
}

/* ============================================
   FIELD ERROR
   ============================================ */
.rv-field-error {
  font-size: 0.75rem;
  color: #ef4444;
  padding-left: 4px;
  margin-top: 4px;
}

.rv-error-list {
  margin: 0;
  padding-left: 16px;
  list-style: disc;
}

.rv-error-list li {
  padding: 1px 0;
}

/* ============================================
   SECTION ACTIONS (buttons row)
   ============================================ */
.section-actions {
  display: flex;
  gap: 12px;
  padding-top: 8px;
}

/* ============================================
   BUTTONS
   ============================================ */
.rv-btn {
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: 'Lato', sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.rv-btn--primary {
  background: #56bde8;
  color: #fff;
  border: none;
}

.rv-btn--primary:hover {
  background: #3dadd8;
  box-shadow: 0 4px 12px rgba(86, 189, 232, 0.3);
}

.rv-btn--primary:disabled {
  background: #94a3b8;
  cursor: not-allowed;
  box-shadow: none;
}

.rv-btn--secondary {
  background: #fff;
  color: #64748b;
  border: 1.5px solid #d1d5db;
}

.rv-btn--secondary:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

/* ============================================
   INPUT OVERRIDES
   ============================================ */
.rv-input >>> .v-input__slot {
  background: #fff !important;
  border: 1px solid #e2e4e8 !important;
  border-radius: 10px !important;
  min-height: 42px !important;
  transition: all 0.15s ease;
}

.rv-input >>> .v-input__slot:hover {
  border-color: #c9cdd3 !important;
}

.rv-input >>> .v-input__slot::before,
.rv-input >>> .v-input__slot::after {
  display: none !important;
}

.rv-input >>> .v-text-field__slot input,
.rv-input >>> .v-select__selection {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.85rem !important;
  font-weight: 600 !important;
  color: #1a1e24 !important;
}

.rv-input >>> .v-label {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.82rem !important;
  color: #94a3b8 !important;
}

.rv-input >>> .v-text-field__details {
  padding: 0 4px;
}

/* ============================================
   TRANSITIONS
   ============================================ */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (max-width: 768px) {
  .rv-page {
    padding: 16px 12px 32px;
  }

  .rv-header__title {
    font-size: 1rem;
  }

  .step-indicator {
    padding: 20px 16px;
  }

  .step-indicator__step {
    min-width: 80px;
  }

  .step-indicator__label {
    font-size: 0.62rem;
    max-width: 80px;
  }

  .step-indicator__circle {
    width: 34px;
    height: 34px;
    font-size: 0.78rem;
  }

  .step-indicator__line {
    margin-top: 17px;
  }

  .detail-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .detail-label {
    flex: none;
  }

  .detail-val {
    width: 100%;
  }

  .section-body {
    padding: 16px;
  }
}

/* ============================================
   DARK MODE
   ============================================ */
.dark-mode .rv-page { background-color: var(--app-page); }
.dark-mode .rv-header { background: var(--app-card); }
.dark-mode .rv-header__title { color: var(--app-text); }
.dark-mode .rv-header__back { background: var(--app-form-tile); border-color: var(--app-form-tile-border); }
.dark-mode .rv-header__back:hover { background: var(--app-form-tile-hover); }
.dark-mode .rv-header__action { background: var(--app-form-tile); border-color: var(--app-form-tile-border); }
.dark-mode .rv-header__action:hover { background: var(--app-form-tile-hover); }
.dark-mode .section-card { background: var(--app-card); }
.dark-mode .section-body { background: var(--app-card); }
.dark-mode .detail-label { color: var(--app-text-muted); }
.dark-mode .detail-val { background: var(--app-form-tile); border-color: var(--app-form-tile-border); color: var(--app-text); }
.dark-mode .step-indicator { background: var(--app-form-tile); }
.dark-mode .step-indicator__circle { background: var(--app-border); color: var(--app-text-muted); border-color: var(--app-border-strong); }
.dark-mode .step-indicator__label { color: var(--app-text-muted); }
.dark-mode .step-indicator__line { background: var(--app-border-strong); }
.dark-mode .file-card { background: var(--app-form-tile); border-color: var(--app-form-tile-border); }
.dark-mode .file-card__name { color: var(--app-text); }
.dark-mode .file-card__hint { color: var(--app-text-faint); }
.dark-mode .file-card__btn { background: var(--app-form-tile); color: var(--app-text-muted); }
.dark-mode .rv-input >>> .v-input__slot { background: var(--app-field) !important; border-color: var(--app-field-border) !important; }
.dark-mode .rv-input >>> .v-text-field__slot input,
.dark-mode .rv-input >>> .v-select__selection { color: var(--app-text) !important; }
.dark-mode .rv-btn--secondary { background: var(--app-form-tile); color: var(--app-text-muted); border-color: var(--app-border); }
.dark-mode .upload-divider { background: var(--app-border); }
.dark-mode .upload-group__label { color: var(--app-text-muted); }
.dark-mode .rv-revert-card { background: var(--app-form-tile); }
.dark-mode .rv-revert-card__title { color: var(--app-text); }
.dark-mode .rv-revert-card__label { color: var(--app-text-muted); }

/* >>> dark companions: the light rules above pin these to a dark ink
   with !important, which on the dark field renders the text invisible
   rather than absent. Token references only. <<< */

.dark-mode .rv-input >>> .v-text-field__slot input,
.dark-mode .rv-input >>> .v-select__selection {
  color: var(--app-text) !important;
}

</style>
